import { SelectCheckbox } from '@advanza/input'
import { AlertTriangle, Download01, RefreshCw02, SearchMd, XCircle } from '@untitledui/icons'
import clsx from 'clsx'
import { formatEuro } from 'components/billing/debtorNew/format'
import { Card } from 'components/billing/debtorNew/ui'
import ListItemSelector from 'components/ListItemSelector'
import { format } from 'date-fns'
import { useAppSelector } from 'hooks/hooks'
import { useSepaCreation } from 'hooks/sepaBatchHooks'
import { useState } from 'react'

/**
 * Lists the credit invoices that still need a SEPA payout (negative amount,
 * not settled, not in a batch yet) and creates the batch: the file downloads
 * immediately and the invoices move to the overview tab as `sepa_created`.
 */
const SepaCreationTab = () => {
    // One SEPA file per country (own bank account per country); the page
    // follows the country switcher, like the old button on the invoices page.
    const { countries } = useAppSelector((state) => state.countries)
    const countryCode = countries.length === 1 ? countries[0] : undefined
    const {
        invoices,
        count,
        totalAmount,
        skippedInvoices,
        isFetching,
        isCreating,
        error,
        createBatch,
    } = useSepaCreation(countryCode)
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [isConfirming, setIsConfirming] = useState(false)
    const [search, setSearch] = useState('')
    const [selectedMonths, setSelectedMonths] = useState<string[]>([])

    // Only months that actually have an open credit note are offered, newest
    // first. Multi-select so Finance can batch several months in one file.
    const monthLabels = new Map<string, string>()
    invoices.forEach((invoice) => {
        const date = new Date(invoice.datetime)
        monthLabels.set(format(date, 'yyyy-MM'), format(date, 'MMMM yyyy'))
    })
    const monthOptions = [...monthLabels.keys()]
        .sort()
        .reverse()
        .map((month) => ({ value: month, name: monthLabels.get(month) }))
    // A selected month can disappear from the list (batch created, country
    // switched); ignore it instead of filtering everything into an empty,
    // un-uncheckable state.
    const activeMonths = selectedMonths.filter((month) => monthLabels.has(month))

    const query = search.trim().toLowerCase()
    const hasFilter = query !== '' || activeMonths.length > 0
    const visibleInvoices = hasFilter
        ? invoices.filter((invoice) => {
              const matchesQuery =
                  !query ||
                  invoice.invoice_nr.toLowerCase().includes(query) ||
                  (invoice.provider?.business_name || '').toLowerCase().includes(query)
              const matchesMonth =
                  activeMonths.length === 0 ||
                  activeMonths.includes(format(new Date(invoice.datetime), 'yyyy-MM'))
              return matchesQuery && matchesMonth
          })
        : invoices

    // Select-all follows the current filters, so Finance can filter down to a
    // month or provider and grab exactly those invoices in one click.
    const visibleSelectableIds = visibleInvoices
        .filter((invoice) => invoice.has_bank_details)
        .map((invoice) => invoice.invoice_id)
    const allVisibleSelected =
        visibleSelectableIds.length > 0 &&
        visibleSelectableIds.every((id) => selectedIds.includes(id))
    const missingBankDetailsCount = invoices.filter((invoice) => !invoice.has_bank_details).length
    const selectedTotal = invoices
        .filter((invoice) => selectedIds.includes(invoice.invoice_id))
        .reduce((sum, invoice) => sum + Math.abs(invoice.amount_netto), 0)

    const toggleAllVisible = () =>
        setSelectedIds((current) =>
            allVisibleSelected
                ? current.filter((id) => !visibleSelectableIds.includes(id))
                : [...new Set([...current, ...visibleSelectableIds])],
        )

    const toggleOne = (invoiceId: number) =>
        setSelectedIds((current) =>
            current.includes(invoiceId)
                ? current.filter((id) => id !== invoiceId)
                : [...current, invoiceId],
        )

    const create = async () => {
        setIsConfirming(false)
        const created = await createBatch(selectedIds)
        if (created) {
            setSelectedIds([])
        }
    }

    if (!countryCode) {
        return (
            <Card className="flex items-center gap-2 px-4 py-8 text-md text-neutral-500">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />A SEPA file is created
                per country. Select a single country in the top bar to create a batch.
            </Card>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-md text-red-700">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            )}

            {skippedInvoices.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-md text-amber-800">
                    <p className="flex items-center gap-2 font-semibold">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        The batch was created, but {skippedInvoices.length} invoices were skipped
                        because their bank details failed SEPA validation. They are not in the file
                        and got no payout.
                    </p>
                    <ul className="mt-1.5 flex flex-col gap-0.5 pl-6 text-sm text-amber-700">
                        {skippedInvoices.map((skipped) => (
                            <li key={skipped.invoice_id} className="tabular-nums">
                                {skipped.invoice_nr} — {skipped.reason}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <p className="text-md text-neutral-500">
                    <span className="font-semibold tabular-nums text-dark">{count}</span> open
                    credit notes for <span className="font-semibold text-dark">{countryCode}</span>
                    <span className="tabular-nums"> · {formatEuro(totalAmount)}</span>
                </p>
                {missingBankDetailsCount > 0 && (
                    <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-sm font-medium text-amber-700">
                        <AlertTriangle className="h-3 w-3" />
                        {missingBankDetailsCount} without bank details
                    </span>
                )}
                <div className="ml-auto flex items-center gap-3">
                    {selectedIds.length > 0 && (
                        <span className="rounded-full bg-primary-50 px-2.5 py-1 text-md font-medium tabular-nums text-primary-700">
                            {selectedIds.length} selected · {formatEuro(selectedTotal)}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setIsConfirming(true)}
                        disabled={selectedIds.length === 0 || isCreating}
                        className={clsx(
                            'flex items-center gap-1.5 rounded-lg px-3 py-2 text-md font-semibold transition',
                            selectedIds.length === 0 || isCreating
                                ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                                : 'bg-primary text-white hover:opacity-90',
                        )}>
                        {isCreating ? (
                            <>
                                <RefreshCw02 className="h-4 w-4 animate-spin" />
                                Creating…
                            </>
                        ) : (
                            <>
                                <Download01 className="h-4 w-4" />
                                Create SEPA batch
                            </>
                        )}
                    </button>
                </div>
            </div>

            {isConfirming && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-md font-semibold text-amber-800">
                        Create a SEPA batch for {selectedIds.length} invoices (
                        {formatEuro(selectedTotal)}, {countryCode})?
                    </p>
                    <p className="mt-0.5 text-sm text-amber-700">
                        The XML file downloads immediately and the invoices are set to sepa_created.
                        Upload the file to the bank afterwards.
                    </p>
                    <div className="mt-2.5 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={create}
                            className="rounded-lg bg-primary px-3 py-2 text-md font-semibold text-white transition hover:opacity-90">
                            Yes, create batch
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsConfirming(false)}
                            className="rounded-lg bg-white px-3 py-2 text-md font-semibold text-neutral-600 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <Card>
                <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-2.5">
                    <SearchMd className="h-4 w-4 shrink-0 text-neutral-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Filter by invoice number or provider…"
                        className="w-full border-none bg-transparent text-md text-dark outline-none placeholder:text-neutral-400"
                    />
                    {hasFilter && (
                        <span className="shrink-0 text-sm tabular-nums text-neutral-400">
                            {visibleInvoices.length} of {invoices.length}
                        </span>
                    )}
                    <div className="shrink-0">
                        <SelectCheckbox
                            options={monthOptions}
                            value={activeMonths}
                            onChangeValue={setSelectedMonths}
                            placeholder="All months"
                        />
                    </div>
                </div>

                {(() => {
                    if (isFetching && invoices.length === 0) {
                        return (
                            <p className="flex items-center justify-center gap-2 px-4 py-10 text-md text-neutral-400">
                                <RefreshCw02 className="h-4 w-4 animate-spin" />
                                Loading credit notes…
                            </p>
                        )
                    }
                    if (visibleInvoices.length === 0) {
                        return (
                            <p className="px-4 py-10 text-center text-md text-neutral-400">
                                {hasFilter
                                    ? 'No invoices match the filter.'
                                    : 'No credit notes waiting for a payout. All done.'}
                            </p>
                        )
                    }
                    return (
                        <table className="w-full text-md">
                            <thead>
                                <tr className="border-b border-neutral-100 text-left text-sm uppercase tracking-wide text-neutral-400">
                                    <th
                                        className="w-10 px-4 py-2"
                                        title="Select all listed invoices with valid bank details">
                                        <ListItemSelector
                                            isSelected={allVisibleSelected}
                                            select={toggleAllVisible}
                                        />
                                    </th>
                                    <th className="px-4 py-2 font-medium">Invoice</th>
                                    <th className="px-4 py-2 font-medium">Provider</th>
                                    <th className="px-4 py-2 font-medium">Date</th>
                                    <th className="px-4 py-2 text-right font-medium">Payout</th>
                                    <th className="px-4 py-2 font-medium" />
                                </tr>
                            </thead>
                            <tbody>
                                {visibleInvoices.map((invoice) => {
                                    const isSelected = selectedIds.includes(invoice.invoice_id)
                                    return (
                                        <tr
                                            key={invoice.invoice_id}
                                            onClick={() =>
                                                invoice.has_bank_details &&
                                                toggleOne(invoice.invoice_id)
                                            }
                                            className={clsx(
                                                'border-b border-neutral-50 transition-colors',
                                                invoice.has_bank_details
                                                    ? 'cursor-pointer hover:bg-neutral-50'
                                                    : 'opacity-60',
                                                isSelected && 'bg-primary-50 hover:bg-primary-50',
                                            )}>
                                            <td
                                                className="px-4 py-2"
                                                onClick={(event) => event.stopPropagation()}>
                                                <ListItemSelector
                                                    isSelected={isSelected}
                                                    select={() => toggleOne(invoice.invoice_id)}
                                                    disabled={!invoice.has_bank_details}
                                                />
                                            </td>
                                            <td className="px-4 py-2 font-medium tabular-nums text-dark">
                                                {invoice.invoice_nr}
                                            </td>
                                            <td className="px-4 py-2 text-neutral-600">
                                                {invoice.provider?.business_name ||
                                                    `#${invoice.service_provider_id}`}
                                            </td>
                                            <td className="px-4 py-2 tabular-nums text-neutral-400">
                                                {format(new Date(invoice.datetime), 'd MMM yyyy')}
                                            </td>
                                            <td className="px-4 py-2 text-right font-medium tabular-nums text-dark">
                                                {formatEuro(Math.abs(invoice.amount_netto))}
                                            </td>
                                            <td className="px-4 py-2">
                                                {!invoice.has_bank_details && (
                                                    <span className="flex items-center gap-1 whitespace-nowrap text-sm text-amber-600">
                                                        <AlertTriangle className="h-3.5 w-3.5" />
                                                        missing bank details
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )
                })()}

                {count > invoices.length && (
                    <p className="border-t border-neutral-100 px-4 py-2 text-sm text-neutral-400">
                        Showing the oldest {invoices.length} of {count} — create a batch to make
                        room for the rest.
                    </p>
                )}
            </Card>
        </div>
    )
}

export default SepaCreationTab
